import * as API from '@/API';
import Button from '@/Components/Button/Button';
import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import CircularProgress from '@/Components/CircularProgress/CircularProgress';
import HeaderTitle from '@/Components/Header/HeaderTitle/HeaderTitle';
import MultiSelect from '@/Components/MultiSelect/MultiSelect';
import { FooterActions, FooterButtons, PageFooter } from '@/Components/PageFooter/PageFooter';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { TextBlockInsertable } from '@/Models/TextBlock/TextBlockModel';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import TextBlockDetailStore from '@/Stores/TextBlockDetailStore';
import { useTheme } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import type { Editor as TinyMCEEditor } from 'tinymce';
import Styles from './TextBlockDetail.module.scss';
import { DobInput } from '@dedalusdiit/dob-components';

function TextBlockDetail({ initialState, isEdit }: Readonly<{ initialState: any; isEdit: boolean }>): React.ReactElement {
    const { t } = useTranslation();
    const theme = useTheme();
    const { removeActive } = useFilingCabinetContext();

    const fcContext = useFilingCabinetContext();
    const { id } = useParams<{ id: string }>();
    const currentId = id ? Number.parseInt(id) : 0;
    const store = useMemo(() => new TextBlockDetailStore(new FilingCabinetStore(initialState, fcContext), t, isEdit, currentId), [initialState]);

    const editorRef = useRef<TinyMCEEditor | null>(null);

    const onRadiologistCheckChange = (e: React.ChangeEvent<HTMLInputElement>, radiologistID: number): void => {
        const selectedRadiologists = store.state.textBlock.SelectedRadiologists ?? [];
        let updatedRadiologists;

        if (e.target.checked) {
            updatedRadiologists = [...selectedRadiologists, radiologistID];
        }
        else {
            updatedRadiologists = selectedRadiologists.filter((rad) => rad !== radiologistID);
        }

        store.setSelectedRadiologists(updatedRadiologists);
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>, property: string): void => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        store.setTextBlock({
            ...store.state.textBlock,
            [property]: value,
        });
    };

    const saveAndClose = (): void => {
        if (!store.validateFields()) {
            return;
        }

        store.setIsButtonsDisabled(true);

        if (isEdit) {
            void API.textblocks.editTextBlock(currentId, store.state.textBlock).then((res) => {
                if (res.Error) {
                    store.setShowDialogError(true);
                    store.setDialogErrorMessage(res.Message);
                    store.setIsButtonsDisabled(false);
                }
                else {
                    void removeActive();
                }
            }).catch((err) => console.error('Error updating Text block:', err));
        }
        else {
            void API.textblocks.addNewTextBlock(store.state.textBlock).then((res) => {
                if (res.Error) {
                    store.setShowDialogError(true);
                    store.setDialogErrorMessage(res.Message);
                    store.setIsButtonsDisabled(false);
                }
                else {
                    void removeActive();
                }
            }).catch((err) => console.error('Error adding Text block:', err));
        }
    };

    const deleteTextBlock = (): void => {
        void API.textblocks.deleteTextBlock(currentId).then(removeActive);
    };

    const setupHeaderstyle = (): void => {
        fetch('/api/user/details')
            .then(async (res) => await res.json())
            .then((res) => {
                const styles = [];
                if (res.HeaderBold) {
                    styles.push('bold');
                }
                if (res.HeaderUnderline) {
                    styles.push('underline');
                }
                if (res.HeaderItalic) {
                    styles.push('italic');
                }
                if (res.HeaderUppercase) {
                    styles.push('uppercase');
                }
                store.setHeaderStyles(styles);
            })
            .catch((e) => console.error('Error fetching user details for Header Styles:', e));
    };

    const encodeCustomTags = (content: string): string => {
        if (!content) {
            return '';
        }

        return content.replace(
            /(<|<\/)(ProcedureCodes|ProcedureDescriptions|Dose information|Clinical info|Diagnostic question)(\s*\/?>)/g,
            '&lt;$2$3',
        );
    };

    if (!store.state.isLoaded) {
        return <CircularProgress />;
    }

    return (
        <div className={Styles.detailOverview}>
            <div className={Styles.dataContainer}>
                <HeaderTitle title={isEdit ? `${t('DetailTextBlock')} - ${store.state.textBlock.Name ?? ''}` : t('AddNewTextBlock')} />
                <div className={Styles.formWrapper}>
                    <div className={Styles.formContainer}>
                        <div className={Styles.formContainerLeft}>
                            <DobInput
                                placeholder={`${t('Name')} *`}
                                label={`${t('Name')} *`}
                                direction="vertical"
                                value={store.state.textBlock.Name ?? ''}
                                disabled={isEdit && store.state.textBlock.UseInG2Speech}
                                onChange={(e) => onChange(e, 'Name')}
                                error={!store.state.isFormValid.Name}
                            />
                            {!store.state.isFormValid.Name && (<div className={Styles.validationError}>{store.state.validationErrorMessage}</div>)}
                            <br />
                            <DobInput
                                placeholder={`${t('SearchTerm')} *`}
                                label={`${t('SearchTerm')} *`}
                                direction="vertical"
                                value={store.state.textBlock.SearchTerm ?? ''}
                                onChange={(e) => onChange(e, 'SearchTerm')}
                                error={!store.state.isFormValid.SearchTerm}
                            />
                            {!store.state.isFormValid.SearchTerm && (<div className={Styles.validationError}>{store.state.validationErrorMessage}</div>)}

                            <hr />

                            <div>
                                <div className={Styles.rawInput}>
                                    <label>
                                        <input type="checkbox" checked={store.state.textBlock.UseInReporting} onChange={(e) => onChange(e, 'UseInReporting')} />
                                        {' '}
                                        {t('UseInReporting')}
                                    </label>
                                </div>
                                <div className={Styles.rawInput}>
                                    <label>
                                        <input type="checkbox" checked={store.state.textBlock.UseInG2Speech} onChange={(e) => onChange(e, 'UseInG2Speech')} />
                                        {' '}
                                        {t('UseInG2Speech')}
                                    </label>
                                </div>

                                <br />
                                <hr />
                                <br />

                                <div className={Styles.rawInput}>Text block is applicable for:</div>
                                <div>
                                    <div className={Styles.rawInput}>
                                        <label>
                                            <input type="radio" name="applicableFor" defaultChecked={true} checked={store.state.textBlock.ApplicableFor === TextBlockInsertable.AllProcedures} onChange={(_) => store.setApplicableFor(TextBlockInsertable.AllProcedures)} />
                                            {' '}
                                            All procedures
                                        </label>
                                    </div>
                                    <div className={Styles.rawInput}>
                                        <label>
                                            <input type="radio" name="applicableFor" checked={store.state.textBlock.ApplicableFor === TextBlockInsertable.SpecificModalities} onChange={(_) => store.setApplicableFor(TextBlockInsertable.SpecificModalities)} />
                                            {' '}
                                            Procedures with modality type
                                        </label>
                                        {
                                            store.state.textBlock.ApplicableFor === TextBlockInsertable.SpecificModalities
                                            && (
                                                <MultiSelect
                                                    onChange={store.setChosenModalities}
                                                    title={t('Modalities')}
                                                    options={store.state.modalities}
                                                    useContains
                                                    selected={store.state.textBlock.Modalities}
                                                />
                                            )
                                        }
                                    </div>
                                    <div className={Styles.rawInput}>
                                        <label>
                                            <input type="radio" name="applicableFor" checked={store.state.textBlock.ApplicableFor === TextBlockInsertable.SpecificProcedures} onChange={(_) => store.setApplicableFor(TextBlockInsertable.SpecificProcedures)} />
                                            {' '}
                                            Following procedures
                                        </label>
                                        {
                                            store.state.textBlock.ApplicableFor === TextBlockInsertable.SpecificProcedures
                                            && (
                                                <MultiSelect
                                                    onChange={store.setChosenProcedures}
                                                    title={t('Procedures')}
                                                    options={store.state.procedures}
                                                    useContains
                                                    selected={store.state.textBlock.Procedures}
                                                />
                                            )
                                        }
                                    </div>
                                </div>
                                <br />
                                <div className={Styles.rawInput}>
                                    <label>
                                        <input type="checkbox" checked={store.state.textBlock.AutoInsert} onChange={(e) => onChange(e, 'AutoInsert')} />
                                        {' '}
                                        {t('AutoInsertInReport')}
                                    </label>
                                </div>
                            </div>

                            <br />
                            <hr />
                            <br />

                            <div>
                                <label>{t('Language')}</label>
                                <div className={Styles.rawInput}>
                                    <label>
                                        <input type="radio" value="NL" checked={store.state.textBlock.Language === 'NL'} onChange={(e) => store.setLanguage(e.target.value)} />
                                        {' '}
                                        {t('Dutch')}
                                    </label>
                                </div>
                                <div className={Styles.rawInput}>
                                    <label>
                                        <input type="radio" value="FR" checked={store.state.textBlock.Language === 'FR'} onChange={(e) => store.setLanguage(e.target.value)} />
                                        {' '}
                                        {t('French')}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className={Styles.formContainerRight}>
                            <DobInput
                                placeholder={t('Pronunciation')}
                                label={t('Pronunciation')}
                                direction="vertical"
                                value={store.state.textBlock.Pronunciation ?? ''}
                                onChange={(e) => onChange(e, 'Pronunciation')}
                            />

                            <br />
                            <hr />
                            <br />

                            <div className={Styles.useForRadiologistsWrapper}>
                                <span>{t('UseForRadiologists')}:</span>
                                <div className={Styles.checkAllButtons}>
                                    <Button onClick={store.checkAll} onDark silent>{t('CheckAll')}</Button>
                                    <Button onClick={store.uncheckAll} onDark silent>{t('UncheckAll')}</Button>
                                </div>
                            </div>
                            <div className={Styles.checkboxContainer}>
                                {store.state.textBlock.AvailableRadiologists?.map((radiologist) => (
                                    <div key={radiologist.ID} className={Styles.rawInput}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={store.state.textBlock.SelectedRadiologists.includes(radiologist.ID)}
                                                onChange={(e) => onRadiologistCheckChange(e, radiologist.ID)}
                                            />
                                            {radiologist.FullName}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={Styles.tinymce}>
                        <Editor
                            onInit={(_evt, editor) => {
                                editorRef.current = editor;
                            }}
                            onEditorChange={store.setContent}
                            value={encodeCustomTags(store.state.textBlock.Content)}
                            init={{
                                skin_url: theme.palette.mode === 'dark' ? '/tinymce/skins/ui/dobco' : '/tinymce/skins/ui/oxide',
                                content_css: theme.palette.mode === 'dark' ? '/tinymce/skins/content/dobco/content.css' : '',
                                height: 400,
                                menubar: false,
                                toolbar: 'bold italic underline | headerstyle casechange | doseinformation clinicalinformation diagnosticquestion proceduredescription procedurecode',
                                entity_encoding: 'raw',
                                custom_elements: 'ProcedureCodes,ProcedureDescriptions,Dose,Clinical,Diagnostic',
                                extended_valid_elements: 'b,i,strong,em,ProcedureCodes[*],ProcedureDescriptions[*],*[*]',
                                valid_children: '+body[ProcedureCodes|ProcedureDescriptions]',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                force_br_newlines: true,
                                forced_root_block: 'div',
                                formats: {
                                    bold: { inline: 'b' },
                                    italic: { inline: 'i' },
                                    underline: { inline: 'u' },
                                },
                                text_patterns: false,
                                setup: (editor) => {
                                    editor.on('BeforeSetContent', function (e) {
                                        if (e.content) {
                                            e.content = e.content
                                                .replace(/(<|<\/)(ProcedureCodes|ProcedureDescriptions|Dose information|Clinical info|Diagnostic question)(>)/g, '<$2>');
                                        }
                                    });

                                    setupHeaderstyle();
                                    editor.ui.registry.addButton('headerstyle', {
                                        icon: 'typography',
                                        tooltip: t('HeaderStyle'),
                                        onAction: () => {
                                            const bookmark = editor.selection.getBookmark();
                                            store.state.headerStyles.forEach((s) => {
                                                if (s === 'uppercase') {
                                                    editor.selection.setContent(editor.selection.getContent().toUpperCase());
                                                }
                                                else {
                                                    editor.formatter.apply(s);
                                                }
                                            });
                                            editor.selection.moveToBookmark(bookmark);
                                        },
                                    });

                                    editor.ui.registry.addButton('procedurecode', {
                                        icon: 'code-sample',
                                        tooltip: t('AddProcedureCodesTooltip'),
                                        onAction: () => {
                                            editor.execCommand('mceInsertContent', false, '&lt;ProcedureCodes&gt;');
                                        },
                                    });

                                    editor.ui.registry.addIcon('doseinformation', `<img style="width: 18px; height: 18px" src="/icons/icnRadioactiveBlack.png" alt={t('DoseInformation')} />`);
                                    editor.ui.registry.addButton('doseinformation', {
                                        icon: 'doseinformation',
                                        tooltip: t('AddDoseInformationTooltip'),
                                        onAction: () => {
                                            editor.execCommand('mceInsertContent', false, '&lt;Dose information&gt;');
                                        },
                                    });

                                    editor.ui.registry.addButton('clinicalinformation', {
                                        icon: 'info',
                                        tooltip: t('AddClinicalInformationTooltip'),
                                        onAction: () => {
                                            editor.execCommand('mceInsertContent', false, '&lt;Clinical info&gt;');
                                        },
                                    });

                                    editor.ui.registry.addButton('diagnosticquestion', {
                                        icon: 'help',
                                        tooltip: t('AddDiagnosticQuestionTooltip'),
                                        onAction: () => {
                                            editor.execCommand('mceInsertContent', false, '&lt;Diagnostic question&gt;');
                                        },
                                    });

                                    editor.ui.registry.addButton('proceduredescription', {
                                        icon: 'document-properties',
                                        tooltip: t('AddProcedureDescriptionsTooltip'),
                                        onAction: () => {
                                            editor.execCommand('mceInsertContent', false, '&lt;ProcedureDescriptions&gt;');
                                        },
                                    });
                                },
                            }}
                        />

                        {!store.state.isFormValid.Content && (<div className={Styles.validationError}>{store.state.validationErrorMessage}</div>)}
                    </div>
                    {/* <span className={Styles.anchorExample}>G2Speech anchor example: &lt;RSDK.Anchor Key=&quot;h&quot; Name=&quot;History&quot; SpokenAs=&quot;history|history of present illness&quot;&gt;&lt;/RSDK.Anchor&gt;</span> */}
                </div>

                <PageFooter>
                    <FooterActions />
                    <FooterButtons>
                        <>{isEdit && <Button red disabled={store.state.isButtonsDisabled} onClick={() => store.setDialogIsOpen(true)} onDark>{t('Delete')}</Button>}</>
                        <Button disabled={store.state.isButtonsDisabled} onClick={() => {
                            void removeActive(); 
                        }} onDark silent>{t('Close')}</Button>
                        <Button disabled={store.state.isButtonsDisabled} onClick={saveAndClose} mediumGreen>{isEdit ? t('SaveAndClose') : t('Add') }</Button>
                    </FooterButtons>
                </PageFooter>
            </div>

            <Dialog open={store.state.dialogIsOpen}>
                <DialogTitle>{t('AreYouSureDeleteTextBlock')}</DialogTitle>
                <DialogButtons>
                    <Button mediumGreen onClick={deleteTextBlock}>{`${t('Delete')} ${t('TextBlock')}`}</Button>
                    <Button onDark silent onClick={() => store.setDialogIsOpen(false)}>{t('Cancel')}</Button>
                </DialogButtons>
            </Dialog>

            <Dialog open={store.showErrorDialog}>
                <DialogTitle>{t('Error')}</DialogTitle>
                <DialogContent>
                    <div>
                        {store.errorDialogMessage}
                    </div>
                </DialogContent>
                <DialogButtons>
                    <Button onDark silent onClick={() => store.setShowDialogError(false)}>{t('Close')}</Button>
                </DialogButtons>
            </Dialog>
        </div>
    );
}

export default observer(TextBlockDetail);
